import { useEventLogging } from '@AppModels/Logging';
import { AutomationsWorkflowGraphic } from '@WebComponents/svgs/graphics/AutomationsWorkflowGraphic';
import { CircularAddIcon } from '@WebComponents/svgs/icons/CircularAddIcon';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ILocationState, InternalAccountIds } from '../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { SupportedIndustries } from '../../../models/MessageTemplates';
import { getIndustryName } from '../../../models/UiUtils';
import { useAutomationsOnHoldQuery } from '../../../queries/Automation/queries/useAutomationsOnHoldQuery';
import { useAutomationTemplatesQuery } from '../../../queries/Automation/queries/useAutomationTemplatesQuery';
import { useSystemAutomationTemplatesQuery } from '../../../queries/Automation/queries/useSystemAutomationTemplatesQuery';
import * as Api from '../../../viewmodels/AppViewModels';
import { Industry, ResourceSelectorId, TemplateStatus } from '../../../viewmodels/AppViewModels';
import { AutomationFilters } from '../../components/AutomationFilters';
import { AutomationCard } from '../../components/automation/AutomationCard';
import { AutomationReportingCard } from '../../components/automation/AutomationReportingCard';
import { AutomationsPendingCard } from '../../components/automation/AutomationsPendingCard';
import { LoadingLogo } from '../../components/LoadingLogo';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { DefaultSelectBox, ISelectBoxOption } from '../../components/SelectBox';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { darkGrayFontColor } from '../../styles/colors';
import { baseStyleSheet, bs } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

interface IAutomationsProps {
	className?: string;
	createAutomationButtonStyles?: StyleDeclarationValue[];
	headerStyles?: StyleDeclarationValue[];
	hideAppBarHeader?: boolean;
	hideDescription?: boolean;
	hideOnHoldAutomations?: boolean;
	/** Impersonation context for admin viewing account automations */
	impersonationContext?: Api.IImpersonationContext;
	onRenderHeader?(): React.ReactNode;
	/** Return true if consumer will handle creation */
	onRequestCreate?(): boolean;
	/** Return true if consumer will handle editing */
	onRequestEdit?(automationTemplate: Api.IAutomationTemplateReference): boolean;
	styles?: StyleDeclarationValue[];
}

const IndustrySelectBoxOptions = SupportedIndustries.map<ISelectBoxOption<Industry>>(x => {
	return {
		title: getIndustryName(x),
		value: x as Industry,
	};
});

const _Automations: React.FC<IAutomationsProps> = props => {
	const {
		className: _className,
		createAutomationButtonStyles = [],
		headerStyles = [],
		hideAppBarHeader,
		hideDescription,
		hideOnHoldAutomations,
		impersonationContext: propsImpersonationContext,
		onRenderHeader: _onRenderHeader,
		onRequestCreate,
		onRequestEdit,
		styles,
	} = props;

	// Hooks
	const history = useHistory();
	const location = useLocation();
	const match = useRouteMatch();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const { logEvent, logInput, logApiError } = useEventLogging('Automations');

	// State
	const [industry, setIndustry] = useState<Industry | undefined>();
	const [searchText, setSearchText] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);

	// Determine if this is the reporting view
	const isReporting = match.path.includes('reporting');

	// Get impersonation context - prefer prop (available on first render) over session (set in useEffect)
	const impersonationContext = propsImpersonationContext || userSession?.impersonationContext;

	// Query: Load automation templates by status (matching original ViewModel pattern)
	// The backend requires separate calls for each status type
	const {
		data: publishedTemplatesData,
		isLoading: isLoadingPublished,
		error: publishedError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Published,
		forAnyTrigger: true,
		impersonationContext,
	});

	const {
		data: disabledTemplatesData,
		isLoading: isLoadingDisabled,
		error: disabledError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Disabled,
		forAnyTrigger: true,
		impersonationContext,
	});

	const {
		data: draftTemplatesData,
		isLoading: isLoadingDraft,
		error: draftError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Draft,
		forAnyTrigger: true,
		impersonationContext,
	});

	// Combine all templates
	const allTemplatesData = useMemo(() => {
		return [...(publishedTemplatesData || []), ...(disabledTemplatesData || []), ...(draftTemplatesData || [])];
	}, [publishedTemplatesData, disabledTemplatesData, draftTemplatesData]);

	const isLoadingAllTemplates = isLoadingPublished || isLoadingDisabled || isLoadingDraft;
	const allTemplatesError = publishedError || disabledError || draftError;

	// Query: Load industry/system templates from automationTemplate/system endpoint
	const { data: industryTemplatesData, isLoading: isLoadingIndustryTemplates } = useSystemAutomationTemplatesQuery({
		industry,
		enabled: true,
		impersonationContext,
	});

	// Query: Load automations on hold (conditional)
	const {
		data: automationsOnHoldData,
		isLoading: isLoadingOnHold,
		error: onHoldError,
	} = useAutomationsOnHoldQuery({
		enabled: !hideOnHoldAutomations && userSession?.account?.features?.automation?.enabled === true,
		impersonationContext,
	});

	// Combine loading states
	const loading = isLoadingAllTemplates || isLoadingIndustryTemplates;

	// Effect: Log initial load event
	useEffect(() => {
		if (!isLoadingAllTemplates && !allTemplatesError) {
			logEvent('TemplatesLoad');
		}
	}, [isLoadingAllTemplates, allTemplatesError, logEvent]);

	// Effect: Handle all templates error
	useEffect(() => {
		if (allTemplatesError) {
			logApiError('TemplatesLoad-Error', allTemplatesError as any);
			errorMessages.pushApiError(allTemplatesError as any);
		}
	}, [allTemplatesError, logApiError, errorMessages]);

	// Effect: Handle on-hold error
	useEffect(() => {
		if (onHoldError) {
			logApiError('LoadAutomationsOnHold-Error', onHoldError as any);
			errorMessages.pushApiError(onHoldError as any);
		}
	}, [onHoldError, logApiError, errorMessages]);

	// Effect: Navigate to pending automations if location.state is 'pending'
	useEffect(() => {
		if (location.state === 'pending' && !hideOnHoldAutomations && automationsOnHoldData && !isLoadingOnHold) {
			if (userSession?.account?.features?.automation?.enabled) {
				// For backward compatibility, pass the data in viewModel
				const locationState: ILocationState<any, any> = {
					viewModel: automationsOnHoldData as any,
				};

				fullscreenModal.history.push({
					pathname: `/automationsonhold/${ResourceSelectorId.AutomationsOnHold}`,
					state: locationState,
				});
			}
		}
	}, [location.state, hideOnHoldAutomations, automationsOnHoldData, isLoadingOnHold, userSession, fullscreenModal]);

	// Categorize templates by status
	const { activeTemplates, inactiveTemplates, industryTemplates } = useMemo(() => {
		const allTemplates = allTemplatesData || [];
		const industryTmpl = industryTemplatesData || [];

		return {
			activeTemplates: allTemplates.filter(t => t && t.status === TemplateStatus.Published),
			inactiveTemplates: allTemplates.filter(
				t => t && (t.status === TemplateStatus.Disabled || t.status === TemplateStatus.Draft)
			),
			industryTemplates: industryTmpl,
		};
	}, [allTemplatesData, industryTemplatesData]);

	// Effect: Search debouncing
	useEffect(() => {
		const timer = setTimeout(() => {
			setSearchText(searchInput);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput]);

	// Filtering function
	const getFilteredTemplates = useCallback(
		(templates: any[]) => {
			return templates.filter(template => {
				// Search text filter - case insensitive search on automation name
				const searchMatch =
					!searchText || (template.name && template.name.toLowerCase().includes(searchText.toLowerCase()));

				// Creator filter - match by creator ID
				const creatorMatch = !selectedCreatorId || template.creator?.id === selectedCreatorId;

				return searchMatch && creatorMatch;
			});
		},
		[searchText, selectedCreatorId]
	);

	// Apply filtering to each category
	const filteredActiveTemplates = useMemo(
		() => getFilteredTemplates(activeTemplates as any),
		[activeTemplates, getFilteredTemplates]
	);

	const filteredInactiveTemplates = useMemo(
		() => getFilteredTemplates(inactiveTemplates as any),
		[inactiveTemplates, getFilteredTemplates]
	);

	const filteredIndustryTemplates = useMemo(
		() => getFilteredTemplates(industryTemplates as any),
		[industryTemplates, getFilteredTemplates]
	);

	// Compute creator options from all templates
	const creatorOptions = useMemo(() => {
		const creators = new Map<string, string>();
		const options: { title: string; value: string | null }[] = [{ title: 'Show all creators', value: null }];

		// Combine all templates from all categories
		const allTemplates = [...activeTemplates, ...inactiveTemplates, ...industryTemplates];

		// Extract unique creators
		allTemplates.forEach(template => {
			if (template.creator?.id && !creators.has(template.creator.id)) {
				const creatorName =
					template.creator.firstName && template.creator.lastName
						? `${template.creator.firstName} ${template.creator.lastName}`.trim()
						: template.creator.email || 'Unknown Creator';

				creators.set(template.creator.id, creatorName);
				options.push({
					title: creatorName,
					value: template.creator.id,
				});
			}
		});

		// Sort creator options alphabetically (excluding "Show all creators")
		const sortedCreatorOptions = options.slice(1).sort((a, b) => {
			const titleA = typeof a.title === 'string' ? a.title : '';
			const titleB = typeof b.title === 'string' ? b.title : '';
			return titleA.localeCompare(titleB);
		});

		return [options[0], ...sortedCreatorOptions];
	}, [activeTemplates, inactiveTemplates, industryTemplates]);

	// Helper: Compute if a template can be edited
	const canEditTemplate = useCallback(
		(template: Api.IAutomationTemplateReference) => {
			const isAdmin =
				userSession.user.role === Api.EUserRole.SuperAdmin || userSession.user.role === Api.EUserRole.Admin;
			return template.status !== Api.TemplateStatus.Archived && (isAdmin || template.scope === Api.TemplateScope.User);
		},
		[userSession.user.role]
	);

	// Check if user is impersonating
	const isImpersonating = impersonationContext != null;

	// Render helper: Get automation templates by category
	const getAutomationTemplates = useCallback(
		(templates: 'active' | 'inactive' | 'industry') => {
			const filteredTemplates =
				templates === 'active'
					? filteredActiveTemplates
					: templates === 'inactive'
						? filteredInactiveTemplates
						: filteredIndustryTemplates;

			return (
				<>
					{filteredTemplates.map((template: Api.IAutomationTemplateReference) => {
						return !isReporting ? (
							<AutomationCard
								automationTemplate={template}
								canEdit={canEditTemplate(template)}
								isImpersonating={isImpersonating}
								impersonationContext={impersonationContext}
								key={template.id}
								onRequestEdit={onRequestEdit ? () => onRequestEdit(template) || false : undefined}
							/>
						) : (
							<AutomationReportingCard automationTemplate={template} key={template.id} />
						);
					})}
				</>
			);
		},
		[
			filteredActiveTemplates,
			filteredInactiveTemplates,
			filteredIndustryTemplates,
			isReporting,
			canEditTemplate,
			isImpersonating,
			impersonationContext,
			onRequestEdit,
		]
	);

	// Event handlers
	const handleSearchTextChange = useCallback((newSearchInput: string) => {
		setSearchInput(newSearchInput);
	}, []);

	const handleCreatorFilterChange = useCallback((creatorId: string | null) => {
		setSelectedCreatorId(creatorId);
	}, []);

	const onIndustryChanged = useCallback(
		(option: ISelectBoxOption<Industry>) => {
			setIndustry(option.value);
			logEvent('LoadIndustryTemplates', { industry: option.value });
		},
		[logEvent]
	);

	const onCreateButtonClicked = useCallback(() => {
		if (!onRequestCreate?.()) {
			logInput('CreateAutomation', 'Click');
			history?.push({
				pathname: `${match.url}/new`,
			});
		}
	}, [onRequestCreate, logInput, history, match.url]);

	// Permission check
	const canCreate = useMemo(() => {
		return userSession?.isAdmin ?? false;
	}, [userSession]);

	// Render helpers
	const renderFilters = useCallback(() => {
		return (
			<AutomationFilters
				searchText={searchInput || ''}
				selectedCreatorId={selectedCreatorId || null}
				creatorOptions={creatorOptions}
				onSearchTextChange={handleSearchTextChange}
				onCreatorFilterChange={handleCreatorFilterChange}
			/>
		);
	}, [searchInput, selectedCreatorId, creatorOptions, handleSearchTextChange, handleCreatorFilterChange]);

	const renderCreateNewAutomationButton = useCallback(() => {
		const button = (
			<button
				className={css(styleSheet.createNewButton, ...createAutomationButtonStyles)}
				disabled={!!loading}
				onClick={onCreateButtonClicked}
			>
				<CircularAddIcon />
				<span>
					Create New
					<br />
					Automation
				</span>
			</button>
		);

		if (canCreate) {
			return button;
		}

		return null;
	}, [loading, onCreateButtonClicked, canCreate, createAutomationButtonStyles]);

	const renderIndustrySelector = useCallback(() => {
		if (!InternalAccountIds.has(userSession?.account?.id)) {
			return null;
		}

		const selectedOption =
			IndustrySelectBoxOptions.find(
				x => x.value === (industry ? industry : userSession.account?.toJs().additionalInfo.industry)
			) || IndustrySelectBoxOptions.find(x => x.value === Industry.Miscellaneous);

		return (
			<DefaultSelectBox
				className={css(styleSheet.levTemplatesHeaderIndustrySelector)}
				onSelectionChanged={onIndustryChanged}
				options={IndustrySelectBoxOptions}
				optionStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
				selectedOption={selectedOption}
				triggerStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
			/>
		);
	}, [industry, userSession, onIndustryChanged]);

	const renderHeaders = useCallback(() => {
		return (
			<>
				{!hideAppBarHeader && (
					<MultiContainerHeader appBarHeader={<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Automations</h1>} />
				)}
				<AutomationsPendingCard shouldHide={loading || hideOnHoldAutomations} />
				<div className={css(styleSheet.header, ...headerStyles)}>
					{!hideDescription && (
						<>
							<div className={css(styleSheet.description)}>
								<div className={css(styleSheet.descriptionTitle)}>What is an automation?</div>
								<div>
									An automation (in Levitate) can send a series of emails for you, create a series of action items that
									you find yourself repeating constantly, or a mixture of both! We&apos;re here to automate.
								</div>
							</div>
							<AutomationsWorkflowGraphic className={css(styleSheet.headerWorkflowGraphic)} />
						</>
					)}
					{renderCreateNewAutomationButton()}
				</div>
			</>
		);
	}, [
		loading,
		hideOnHoldAutomations,
		hideAppBarHeader,
		hideDescription,
		headerStyles,
		renderCreateNewAutomationButton,
	]);

	// Main render
	return (
		<div className={css(styleSheet.container, ...(styles || []))}>
			{!isReporting ? renderHeaders() : null}
			<div className={css(styleSheet.body)}>
				{loading ? (
					<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter)}>
						<LoadingLogo size={100} />
					</div>
				) : (
					<>
						{!isReporting && renderFilters()}
						<div className={css(styleSheet.cardGrid)}>
							<h3 className={css(styleSheet.cardGridTitle)} style={isReporting ? { color: darkGrayFontColor } : {}}>
								Active Automations
							</h3>
							<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('active')}</div>
						</div>
						<div className={css(styleSheet.cardGrid)}>
							<h3 className={css(styleSheet.cardGridTitle)} style={isReporting ? { color: darkGrayFontColor } : {}}>
								Inactive Automations
							</h3>
							<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('inactive')}</div>
						</div>

						{(industryTemplates.length > 0 || !!InternalAccountIds.has(userSession.account.id)) && !isReporting ? (
							<div className={css(styleSheet.cardGrid)}>
								<h3 className={css(styleSheet.cardGridTitle, styleSheet.sampleAutomationsSectionTitle)}>
									<span>Sample Automations</span>
									{renderIndustrySelector()}
								</h3>
								<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('industry')}</div>
							</div>
						) : null}
					</>
				)}
			</div>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
};

export const Automations = _Automations;
